import { FC, useState, useEffect, useMemo, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Loader2, Check, ChevronDown, AlertCircle } from 'lucide-react'
import { cn } from '../../../lib/utils'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible'
import { Button } from '../../ui/button'
import { getAgentPersona } from './AgentSelector'
import type { ConsultState } from '../../../contexts/ChatContext'

interface ConsultPillProps {
  consult: ConsultState
}

// Injection-safety contract (relay-consult.spec.ts): comment-like text must NEVER
// surface in the pill — a complete HTML comment (which includes any RELAY marker)
// is stripped, and an unclosed trailing `<!--` prefix is masked so it can't flash
// mid-stream or linger on an interrupted consult. Mirrors CasePanelMessage's
// treatment of Casey prose.
const COMMENT_RE = /<!--[\s\S]*?-->/g
const DANGLING_COMMENT_RE = /<!--[\s\S]*$/

/**
 * ConsultPill - Renders an advisory specialist consult as a collapsible "pill"
 * inside Casey's chat turn. Mirrors ThinkingPanel's collapsible idiom so the
 * consult reads as a handoff (Casey ──▶ specialist), not as Casey talking.
 */
export const ConsultPill: FC<ConsultPillProps> = ({ consult }) => {
  const isStreaming = consult.status === 'streaming'
  const [isOpen, setIsOpen] = useState(isStreaming)
  const contentRef = useRef<HTMLDivElement>(null)
  const wasStreaming = useRef(isStreaming)

  const displayContent = useMemo(
    () => consult.content.replace(COMMENT_RE, '').replace(DANGLING_COMMENT_RE, '').trimEnd(),
    [consult.content]
  )

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [consult.content, isStreaming])

  // Auto-expand when streaming starts; collapse to the "Consulted <Specialist>"
  // header on the live streaming→done transition (G6.5). Only the transition
  // collapses — a manual expand afterwards sticks.
  useEffect(() => {
    if (isStreaming) {
      setIsOpen(true)
    } else if (wasStreaming.current) {
      setIsOpen(false)
    }
    wasStreaming.current = isStreaming
  }, [isStreaming])

  const persona = getAgentPersona(consult.agent)
  // Subtle persona-colored left edge for the content box. The box itself stays a
  // calm neutral surface — a full persona gradient (accentFrom/accentTo are
  // full-saturation stops) reads far too loud and tanks text contrast.
  const accentBorder =
    consult.agent === 'hpi-pe-agent' ? 'border-l-emerald-400/60'
    : consult.agent === 'clinical-reasoning' ? 'border-l-amber-400/60'
    : 'border-l-slate-400/60'

  // Handoff chip: Casey ──▶ <avatar> <persona.name>. Shared across all states.
  const handoffChip = (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="font-medium">Casey</span>
      <span aria-hidden="true">──▶</span>
      <div
        className={cn(
          'w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0',
          persona.accentFrom, persona.accentTo
        )}
      >
        <span className="text-[9px] font-bold text-white">{persona.avatar}</span>
      </div>
      <span className="font-medium text-foreground">{persona.name}</span>
    </div>
  )

  // 'dropped' is a static muted row — not expandable, no chevron, no content.
  if (consult.status === 'dropped') {
    return (
      <div className="space-y-1.5">
        {handoffChip}
        <div className="flex items-center gap-2 text-xs text-muted-foreground/70 px-3 h-8">
          <AlertCircle className="h-3.5 w-3.5" />
          <span>Consult unavailable — Casey answered directly</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      {handoffChip}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-8 px-3 text-xs gap-2 rounded-lg',
              'bg-surface hover:bg-surface-elevated',
              'border border-border/50 hover:border-clinical-teal/30',
              'transition-all duration-200',
              isStreaming && 'border-clinical-teal/50 shadow-[0_0_10px_-3px_hsl(var(--clinical-teal)/0.3)]'
            )}
          >
            {isStreaming ? (
              <>
                <Loader2 className="h-3.5 w-3.5 text-clinical-teal animate-spin" />
                <span className="font-medium text-clinical-teal">
                  Consulting {persona.name}…
                </span>
                {consult.reason && (
                  <span className="text-muted-foreground/60">— {consult.reason}</span>
                )}
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5 text-clinical-teal" />
                <span className="font-medium text-muted-foreground">
                  Consulted {persona.name}
                </span>
                <ChevronDown className={cn(
                  'h-3 w-3 transition-transform text-muted-foreground',
                  isOpen && 'rotate-180'
                )} />
              </>
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2 animate-fade-in">
          <div
            ref={contentRef}
            className={cn(
              'relative p-4 rounded-xl rounded-l-sm',
              'bg-muted/40 border border-border/60 border-l-2', accentBorder,
              'max-h-48 overflow-y-auto',
              'scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent'
            )}
          >
            {/* Persona attribution — the pill body reads as the specialist's
                own voice (avatar + name + role), reinforcing the accent color. */}
            <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-border/40">
              <div
                className={cn(
                  'w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0',
                  persona.accentFrom, persona.accentTo
                )}
              >
                <span className="text-[9px] font-bold text-white">{persona.avatar}</span>
              </div>
              <span className="text-xs font-semibold text-foreground">{persona.name}</span>
              <span className="text-[10px] text-muted-foreground/70">· {persona.role}</span>
            </div>
            {consult.reason && (
              <p className="text-xs text-muted-foreground/70 mb-2 italic">
                Casey asked: {consult.reason}
              </p>
            )}
            {/* Markdown (bold, lists, etc.) — a DELIBERATE divergence from the chat
                bubbles, which render plain pre-wrap text: advisory notes carry
                structure (lists, emphasis) worth rendering inside the pill. */}
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
